import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Users, Pencil, Trash2, Plus, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
    { value: 'admin', label: 'Administrador' },
    { value: 'cashier', label: 'Cajero' },
    { value: 'delivery', label: 'Repartidor' },
    { value: 'chef', label: 'Chef / Cocinero' },
    { value: 'waiter', label: 'Mesero' },
    { value: 'support', label: 'Soporte / Atención' },
];

const ROLE_COLORS = {
    owner: 'bg-orange-100 text-orange-700',
    admin: 'bg-blue-100 text-blue-700',
    cashier: 'bg-emerald-100 text-emerald-700',
    delivery: 'bg-violet-100 text-violet-700',
    chef: 'bg-red-100 text-red-700',
    waiter: 'bg-teal-100 text-teal-700',
    support: 'bg-neutral-100 text-neutral-600',
};

const roleLabel = (r) => ROLES.find(x => x.value === r)?.label || r;
const empty = { name: '', role: 'cashier', phone: '', email: '', notes: '', active: true, branch_id: '' };

export default function AliadoSquadPage() {
    const [members, setMembers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [m, b] = await Promise.all([
                api.get('/me/oknegocio/squad'),
                api.get('/me/oknegocio/branches'),
            ]);
            setMembers(m.data);
            setBranches(b.data);
        } catch { toast.error('Error al cargar equipo'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
    const openEdit = (m) => {
        setEditing(m.id);
        setForm({ name: m.name, role: m.role, phone: m.phone || '', email: m.email || '', notes: m.notes || '', active: m.active ?? true, branch_id: m.branch_id || '__none__' });
        setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) return toast.error('El nombre es requerido');
        setSaving(true);
        try {
            const payload = { ...form, branch_id: form.branch_id === '__none__' ? '' : form.branch_id };
            if (editing) {
                await api.put(`/me/oknegocio/squad/${editing}`, payload);
                toast.success('Colaborador actualizado');
            } else {
                await api.post('/me/oknegocio/squad', payload);
                toast.success('Colaborador agregado');
            }
            setShowForm(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/me/oknegocio/squad/${deleteId}`);
            toast.success('Colaborador eliminado');
            setDeleteId(null);
            load();
        } catch { toast.error('Error al eliminar'); }
    };

    const branchName = (id) => branches.find(b => b.id === id)?.name || '—';

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold flex items-center gap-2"><Users className="h-5 w-5 text-orange-600" /> OkSquad</h1>
                    <p className="text-sm text-muted-foreground">{members.length} colaborador(es) en tu equipo.</p>
                </div>
                <Button size="sm" onClick={openCreate} className="bg-orange-600 hover:bg-orange-700">
                    <Plus className="h-4 w-4 mr-1" /> Agregar
                </Button>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground text-center py-8">Cargando…</div>
            ) : members.length === 0 ? (
                <div className="rounded-2xl border bg-card p-8 text-center">
                    <Users className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <div className="text-sm text-muted-foreground">Aún no tienes colaboradores. Agrega a tu equipo para gestionar roles.</div>
                </div>
            ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                    {members.map(m => (
                        <div key={m.id} className="rounded-2xl border bg-card p-4 space-y-2">
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="font-semibold text-sm">{m.name}</div>
                                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${ROLE_COLORS[m.role] || 'bg-neutral-100 text-neutral-600'}`}>
                                        {roleLabel(m.role)}
                                    </span>
                                </div>
                                <Switch checked={m.active} onCheckedChange={async (v) => {
                                    await api.put(`/me/oknegocio/squad/${m.id}`, { active: v });
                                    setMembers(prev => prev.map(x => x.id === m.id ? { ...x, active: v } : x));
                                }} className="scale-75" />
                            </div>
                            {m.phone && <div className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" />{m.phone}</div>}
                            {m.email && <div className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" />{m.email}</div>}
                            {m.branch_id && branches.length > 0 && (
                                <div className="text-xs text-muted-foreground">🏪 {branchName(m.branch_id)}</div>
                            )}
                            {m.notes && <div className="text-xs text-muted-foreground italic">📝 {m.notes}</div>}
                            <div className="flex gap-1 pt-1">
                                <Button size="sm" variant="ghost" onClick={() => openEdit(m)} className="h-7 px-2 text-xs">
                                    <Pencil className="h-3 w-3 mr-1" /> Editar
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setDeleteId(m.id)} className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10">
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>{editing ? 'Editar colaborador' : 'Nuevo colaborador'}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium">Nombre *</label>
                            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" placeholder="Nombre completo" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Rol</label>
                            <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-medium">Teléfono</label>
                            <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="mt-1" placeholder="55 1234 5678" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Email</label>
                            <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="mt-1" placeholder="correo@ejemplo.com" />
                        </div>
                        {branches.length > 0 && (
                            <div>
                                <label className="text-xs font-medium">Sucursal</label>
                                <Select value={form.branch_id || '__none__'} onValueChange={v => setForm(p => ({ ...p, branch_id: v }))}>
                                    <SelectTrigger className="mt-1"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">Sin asignar</SelectItem>
                                        {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-medium">Notas</label>
                            <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="mt-1" placeholder="Observaciones…" />
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch id="mactive" checked={form.active} onCheckedChange={v => setForm(p => ({ ...p, active: v }))} />
                            <label htmlFor="mactive" className="text-xs font-medium">Activo</label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                            {saving ? 'Guardando…' : editing ? 'Guardar' : 'Agregar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>¿Eliminar colaborador?</DialogTitle></DialogHeader>
                    <p className="text-sm text-muted-foreground">Esta acción no se puede deshacer.</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteId(null)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
